// recurrenceToUtcList.ts

export type RecurrenceType = 'daily' | 'weekly' | 'weekday' | 'monthly' | 'yearly';

export type WeeklyDayShort =
  | 'sun'
  | 'mon'
  | 'tue'
  | 'wed'
  | 'thu'
  | 'fri'
  | 'sat'
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export type MonthShort =
  | 'jan' | 'feb' | 'mar' | 'apr' | 'may' | 'jun'
  | 'jul' | 'aug' | 'sep' | 'oct' | 'nov' | 'dec'
  | 'january' | 'february' | 'march' | 'april' | 'june'
  | 'july' | 'august' | 'september' | 'october'
  | 'november' | 'december';

export interface RecurrenceProperties {
  interval?: number;         // bước lặp
  days?: WeeklyDayShort[];   // weekly
  dayOfMonth?: number;       // monthly/yearly
  month?: MonthShort;        // yearly: nếu có thì dùng
  firstDayOfWeek?: WeeklyDayShort;
}

export interface RecurrenceTimeZone {
  name: string;
  offset: number; // phút lệch so với UTC
}

export interface SeriesTime {
  startYear: number;
  startMonth: number; // 1-12
  startDay: number;   // 1-31
  endYear: number;
  endMonth: number;   // 1-12
  endDay: number;     // 1-31
  startTimeMinutes: number;
  durationMinutes: number;
}

export interface RecurrencePayload {
  recurrenceProperties: RecurrenceProperties;
  recurrenceType: RecurrenceType;
  recurrenceTimeZone: RecurrenceTimeZone;
  seriesTime: SeriesTime;
}

export interface OccurrenceRange {
  startDate: string; // UTC ISO
  endDate: string;   // UTC ISO
}

const DAY_MAP: Record<string, number> = {
  sun: 0,
  sunday: 0,
  mon: 1,
  monday: 1,
  tue: 2,
  tuesday: 2,
  wed: 3,
  wednesday: 3,
  thu: 4,
  thursday: 4,
  fri: 5,
  friday: 5,
  sat: 6,
  saturday: 6,
};

const MONTH_MAP: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function normalizeInterval(value: number | undefined): number {
  if (!value || value < 1) return 1;
  return value;
}

/**
 * Generate list of UTC ISO ranges { startDate, endDate } cho:
 * - daily: Every N days
 * - weekday: Every N weekdays (Monday-Friday)
 * - weekly: Every N weeks on selected days
 * - monthly: Every N months on specific day of month
 * - yearly: Every N years on specific month and day
 */
export function generateRecurrenceUtcOccurrences(
  payload: RecurrencePayload,
  maxOccurrences = 500
): OccurrenceRange[] {
  const { recurrenceProperties, recurrenceTimeZone, seriesTime, recurrenceType } =
    payload;

  const interval = normalizeInterval(recurrenceProperties?.interval);
  const offsetMinutes = recurrenceTimeZone.offset;

  const {
    startYear,
    startMonth,
    startDay,
    endYear,
    endMonth,
    endDay,
    startTimeMinutes,
    durationMinutes,
  } = seriesTime;

  const startDateUtcMs = Date.UTC(startYear, startMonth - 1, startDay, 0, 0, 0);
  
  // Kiểm tra xem có end date không (nếu endYear/endMonth/endDay = 0 hoặc không hợp lệ)
  const hasEndDate = endYear > 0 && endMonth > 0 && endDay > 0;
  const endDateUtcMs = hasEndDate 
    ? Date.UTC(endYear, endMonth - 1, endDay, 0, 0, 0)
    : null;

  const results: OccurrenceRange[] = [];

  const dayIndexes: number[] =
    recurrenceType === 'weekly'
      ? (recurrenceProperties.days ?? [])
          .map((d) => DAY_MAP[d.toLowerCase()])
          .filter((n) => typeof n === 'number') as number[]
      : [];

  let currentDateUtcMs = startDateUtcMs;
  let occurrenceCount = 0;

  // Nếu không có end date, chỉ dùng maxOccurrences
  // Nếu có end date, dùng cả hai điều kiện
  const shouldContinue = () => {
    if (hasEndDate && endDateUtcMs !== null) {
      return currentDateUtcMs <= endDateUtcMs && occurrenceCount < maxOccurrences;
    }
    return occurrenceCount < maxOccurrences;
  };

  while (shouldContinue()) {
    const currentDate = new Date(currentDateUtcMs);
    const y = currentDate.getUTCFullYear();
    const m = currentDate.getUTCMonth(); // 0-11
    const d = currentDate.getUTCDate();

    const daysDiff = Math.floor((currentDateUtcMs - startDateUtcMs) / ONE_DAY_MS);

    let isOccurrence = false;

    switch (recurrenceType) {
      case 'daily': {
        isOccurrence = daysDiff >= 0 && daysDiff % interval === 0;
        break;
      }

      case 'weekday': {
        // Weekday = Monday through Friday (1-5 in getUTCDay where 0=Sunday, 6=Saturday)
        const weekday = currentDate.getUTCDay();
        const isWeekday = weekday >= 1 && weekday <= 5;
        
        if (isWeekday && daysDiff >= 0) {
          let weekdayCount = 0;
          let tempDate = startDateUtcMs;
          
          while (tempDate <= currentDateUtcMs) {
            const tempWeekday = new Date(tempDate).getUTCDay();
            if (tempWeekday >= 1 && tempWeekday <= 5) {
              weekdayCount++;
            }
            tempDate += ONE_DAY_MS;
          }
          
          isOccurrence = (weekdayCount - 1) % interval === 0;
        }
        break;
      }

      case 'weekly': {
        if (!dayIndexes.length) break;

        const weekday = currentDate.getUTCDay();
        const weekIndex = Math.floor(daysDiff / 7);
        const inCorrectWeek =
          weekIndex >= 0 && weekIndex % interval === 0;
        const inSelectedDay = dayIndexes.includes(weekday);

        isOccurrence = inCorrectWeek && inSelectedDay;
        break;
      }

      case 'monthly': {
        const startMonthIndex = startMonth - 1;
        const monthsDiff =
          (y - startYear) * 12 + (m - startMonthIndex);

        if (monthsDiff >= 0 && monthsDiff % interval === 0) {
          const targetDayOfMonth =
            recurrenceProperties.dayOfMonth ?? startDay;
          const dim = daysInMonth(y, m);
          const effectiveDay = Math.min(targetDayOfMonth, dim);

          isOccurrence = d === effectiveDay;
        }
        break;
      }

      case 'yearly': {
        const yearsDiff = y - startYear;
        if (yearsDiff >= 0 && yearsDiff % interval === 0) {
          const monthFromProp = recurrenceProperties.month
            ? MONTH_MAP[recurrenceProperties.month.toLowerCase()]
            : undefined;

          const targetMonthIndex =
            typeof monthFromProp === 'number'
              ? monthFromProp
              : startMonth - 1;

          const targetDayOfMonth =
            recurrenceProperties.dayOfMonth ?? startDay;

          isOccurrence = m === targetMonthIndex && d === targetDayOfMonth;
        }
        break;
      }

      default:
        isOccurrence = false;
        break;
    }

    if (isOccurrence) {
      const localHour = Math.floor(startTimeMinutes / 60);
      const localMinute = startTimeMinutes % 60;

      const localAsUtcMs = Date.UTC(
        y,
        m,
        d,
        localHour,
        localMinute,
        0,
        0
      );

      const utcStartMs = localAsUtcMs - offsetMinutes * 60 * 1000;
      const utcEndMs = utcStartMs + durationMinutes * 60 * 1000;

      results.push({
        startDate: new Date(utcStartMs).toISOString(),
        endDate: new Date(utcEndMs).toISOString(),
      });

      occurrenceCount++;
    }

    currentDateUtcMs += ONE_DAY_MS;
  }

  return results;
}
